import {
  encryptRecord,
  decryptRecord,
  hasActiveKey,
  isEncryptedPayload,
} from './crypto';
import {
  isEncryptedTable,
  PLAINTEXT_FIELDS,
  ENC_COLUMN,
  type EncryptedTableName,
} from './encryption-config';

/**
 * G2.27 — Service-layer encryption helpers.
 *
 * Why not Dexie middleware? DBCore middleware runs inside the IDB
 * transaction scope. Any `await` on a non-Dexie Promise (WebCrypto is
 * native) closes that transaction, yielding `InvalidStateError` at the
 * next `tx.objectStore()` call. Dexie.Promise wrapping does not help —
 * the underlying issue is the IDB spec auto-closing txns on microtask
 * yield.
 *
 * Instead, services call `encryptForStorage` BEFORE `.put()` and
 * `decryptFromStorage` AFTER `.get()`. Both helpers run outside any
 * transaction so WebCrypto awaits are free. This is also easier to
 * reason about when reading the services — the split/merge is visible.
 *
 * Field split: everything in `PLAINTEXT_FIELDS[table]` stays plaintext
 * (preserves Dexie indexes); everything else is bundled into a single
 * `_enc: EncryptedPayload` column.
 *
 * Pass-through: with no active key, both helpers are no-ops so the
 * login page and pre-enrolment state keep working.
 */

/**
 * Split a row into plaintext + secret halves and AES-GCM-encrypt the
 * secret half. Returns a Dexie-safe object ready for `.put` / `.add`.
 *
 * Typed as `<T>(row: T) => Promise<T>` so call sites don't need index-
 * signature noise — callers usually have nominal types (`DraftBill`,
 * `CachedProduct`, etc.) and we round-trip the same shape.
 */
export async function encryptForStorage<T extends object>(
  row: T,
  table: EncryptedTableName,
): Promise<T> {
  if (!row || typeof row !== 'object') return row;
  if (!hasActiveKey()) return row;
  if (ENC_COLUMN in row) return row; // already encrypted — leave alone

  const plainFields = PLAINTEXT_FIELDS[table];
  const plainSet = new Set<string>([...plainFields, ENC_COLUMN]);

  const plain: Record<string, unknown> = {};
  const secret: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    if (plainSet.has(k)) plain[k] = v;
    else secret[k] = v;
  }
  if (Object.keys(secret).length === 0) return plain as T;
  plain[ENC_COLUMN] = await encryptRecord(secret);
  return plain as T;
}

/** Bulk variant — parallel encryption of an array of rows. */
export async function encryptManyForStorage<T extends object>(
  rows: T[],
  table: EncryptedTableName,
): Promise<T[]> {
  if (rows.length === 0) return rows;
  return Promise.all(rows.map((r) => encryptForStorage(r, table)));
}

/**
 * Reverse of `encryptForStorage` — decrypt the `_enc` blob and merge
 * its contents back onto the row. Passes through when the row was
 * stored plaintext (e.g. pre-v5 legacy, or written without a key).
 *
 * Returns `undefined` when the row is null/undefined so callers can
 * use `??` / optional chaining naturally.
 */
export async function decryptFromStorage<T extends object>(
  row: T | null | undefined,
): Promise<T | undefined> {
  if (row === null || row === undefined) return undefined;
  if (typeof row !== 'object') return row;
  const enc = (row as Record<string, unknown>)[ENC_COLUMN];
  if (!enc) return row;
  if (!isEncryptedPayload(enc)) return row;
  if (!hasActiveKey()) return row; // no key — return ciphertext shape
  try {
    const decrypted = await decryptRecord<Record<string, unknown>>(enc);
    const { [ENC_COLUMN]: _omit, ...rest } = row as Record<string, unknown>;
    return { ...rest, ...decrypted } as T;
  } catch {
    // Wrong key / tampered row — return as-is so the caller sees the
    // ciphertext shape and can decide to purge.
    return row;
  }
}

/** Bulk variant — parallel decryption. */
export async function decryptManyFromStorage<T extends object>(
  rows: T[],
): Promise<T[]> {
  if (rows.length === 0) return rows;
  const results = (await Promise.all(
    rows.map((r) => decryptFromStorage<T>(r)),
  )) as (T | undefined)[];
  return results.filter((r): r is T => r !== undefined);
}

/** Small re-export so call sites don't need two imports. */
export { isEncryptedTable };
